// src/services/presence/PresenceClient.js
class Emitter {
  constructor() {
    this.handlers = {}
  }
  on(evt, fn) {
    this.handlers[evt] = this.handlers[evt] || []
    this.handlers[evt].push(fn)
    return () => {
      const arr = this.handlers[evt]
      if (arr) this.handlers[evt] = arr.filter(f => f !== fn)
    }
  }
  emit(evt, payload) {
    for (const fn of this.handlers[evt] || []) {
      try { fn(payload) } catch (e) {}
    }
  }
}

class PresenceClient extends Emitter {
  constructor() {
    super()
    this.ws = null
    this.url = null
    this.token = null

    this.reconnectTimer = null
    this.backoffMs = 500
    this.maxBackoffMs = 8000
    this.manuallyClosed = false

    this.authed = false
    this.queue = []

    this.hbTimer = null
    this.hbMs = 15000
  }

  connect({ url, token }) {
    if (!url || !token) return
    this.url = url
    this.token = token
    this.manuallyClosed = false

    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return
    }

    this._open()
  }

  _buildWsUrl() {
    const hasQs = this.url.includes('?')
    const join = hasQs ? '&' : '?'
    return `${this.url}${join}token=${encodeURIComponent(this.token)}&client=softphone`
  }

  _open() {
    this._clearReconnect()
    this._stopHeartbeat()

    this.authed = false
    this.queue = []

    const wsUrl = this._buildWsUrl()
    this.ws = new WebSocket(wsUrl)

    this.ws.onopen = () => {
      this.backoffMs = 500
      this.emit('ws:open')
      // auth is via querystring in your server, so no hello needed
    }

    this.ws.onmessage = (ev) => {
      let msg = null
      try { msg = JSON.parse(ev.data) } catch { return }
      if (!msg?.type) return

      if (msg.type === 'presence:hello') {
        this.authed = true
        this.emit('presence:hello', msg)

        this._startHeartbeat()

        this._flushQueue()
        this.getSnapshot()
        return
      }

      if (msg.type === 'presence:snapshot') {
        this.emit('presence:snapshot', msg)
        return
      }

      if (msg.type === 'presence:update') {
        this.emit('presence:update', msg)
        return
      }

      if (msg.type === 'presence:error') {
        this.emit('presence:error', msg)
        return
      }

      // generic targeted messages (click2dial, chat later, etc.)
      if (msg.type === 'msg') {
        this.emit('msg', msg)
        return
      }

      // forward any other event types (chat:message, chat:edit, etc.)
      this.emit(msg.type, msg)
    }

    this.ws.onclose = () => {
      this.emit('ws:close')
      this._stopHeartbeat()
      this.ws = null
      this.authed = false
      this.queue = []
      if (!this.manuallyClosed) this._scheduleReconnect()
    }

    this.ws.onerror = () => {}
  }

  _startHeartbeat() {
    this._stopHeartbeat()

    // fire one immediately
    this._send({ type: 'presence:heartbeat', source: 'web' })

    this.hbTimer = setInterval(() => {
      this._sendRaw({ type: 'presence:heartbeat', source: 'web' })
    }, this.hbMs)
  }

  _stopHeartbeat() {
    if (this.hbTimer) {
      clearInterval(this.hbTimer)
      this.hbTimer = null
    }
  }

  _scheduleReconnect() {
    this._clearReconnect()
    const wait = this.backoffMs
    this.backoffMs = Math.min(this.backoffMs * 2, this.maxBackoffMs)
    this.reconnectTimer = setTimeout(() => {
      if (this.manuallyClosed) return
      this._open()
    }, wait)
  }

  _clearReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  disconnect() {
    this.manuallyClosed = true
    this._clearReconnect()
    this._stopHeartbeat()
    if (this.ws) {
      try { this.ws.close() } catch {}
    }
    this.ws = null
    this.authed = false
    this.queue = []
  }

  _sendRaw(obj) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return
    this.ws.send(JSON.stringify(obj))
  }

  _send(obj) {
    if (!this.authed) {
      this.queue.push(obj)
      return
    }
    this._sendRaw(obj)
  }

  _flushQueue() {
    if (!this.authed) return
    const items = this.queue.slice(0)
    this.queue = []
    for (const obj of items) this._sendRaw(obj)
  }

  getSnapshot() {
    this._send({ type: 'presence:get' })
  }

  set(status, message) {
    this._send({
      type: 'presence:set',
      status,
      message: message || '',
      source: 'web'
    })
  }
}

export default new PresenceClient()
